import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import showingsService from '../../../../api/services/showings';
import TimeSelection from '../../../../components/TimeSelection/TimeSelection';
import StripeCheckout from './StripeCheckout';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { RootState, useAppDispatch } from '../../../../store/store';
import { fetchShowings } from '../../../../store/slices/showings';
import { useSelector } from 'react-redux';
import { selectShowings } from '../../../../store/selectors/showings';
import { Check } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { listings, ShowingType } from '@prisma/client';
import { DocusealForm } from '@docuseal/react';
import { useAuth } from '../../../../contexts/AuthContext';
import formsService from '../../../../api/services/forms';
import useIsMobile from '../../../../hooks/useIsMobile';
import { getListingTimezone } from '../../../../utils/listingTimezone';

const BROKER_FORM_ID = 5;
const GA_BROKERAGE_DISCLOSURE_FORM_ID = 15;

type StepId = 'times' | 'payment' | 'tourAgreement' | 'gaDisclosure';

interface StepConfig {
  id: StepId;
  label: string;
}

interface Props {
  closeModal: () => void;
  listing: listings;
  showingType?: ShowingType;
  isFinalWalkthrough?: boolean;
}

const FormContainer: React.FC<Props> = ({
  closeModal,
  listing,
  showingType,
  isFinalWalkthrough = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [formSlug, setFormSlug] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const elements = useElements();
  const stripe = useStripe();
  const dispatch = useAppDispatch();
  const showings = useSelector((state: RootState) => selectShowings(state));
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();

  const listingId = listing.id;
  const isGeorgiaListing = listing.state === 'GA';
  const isAlabamaListing = listing.state === 'AL';
  const [listingTimezone, setListingTimezone] = useState<string | undefined>();
  useEffect(() => {
    let cancelled = false;
    getListingTimezone(listing).then((tz) => {
      if (!cancelled) setListingTimezone(tz);
    });
    return () => {
      cancelled = true;
    };
  }, [listing]);

  const formik = useFormik({
    initialValues: {
      proposedTime: new Date(),
      agreementSigned: isFinalWalkthrough || isAlabamaListing,
      gaDisclosureSigned: isFinalWalkthrough || !isGeorgiaListing,
      skipStripe: true, // COMMENTED OUT: Tours are now free - was: isFinalWalkthrough
      coupon: '', // COMMENTED OUT: Coupon system disabled - tours are free
    },
    enableReinitialize: false,
    onSubmit: async (values) => {
      try {
        await showingsService.post(
          {
            listings: {
              connect: {
                id: listingId,
              },
            },
            accepted: false,
            proposedTime: values.proposedTime,
            isAgent: false,
            stripePaymentIntentId: paymentIntentId || undefined,
            type: showingType || ShowingType.TOUR,
          },
          true
        );
        setSuccess(true);
        dispatch(fetchShowings());
      } catch (e) {
        /* empty */
      }
    },
  });

  // Determine which steps to show based on conditions
  const steps = useMemo<StepConfig[]>(() => {
    const result: StepConfig[] = [{ id: 'times', label: 'Select Times' }];

    if (!isFinalWalkthrough && !isAlabamaListing) {
      result.push({ id: 'tourAgreement', label: 'Tour Agreement' });
    }

    if (!isFinalWalkthrough && isGeorgiaListing) {
      result.push({ id: 'gaDisclosure', label: 'GA Disclosure' });
    }

    // COMMENTED OUT: Payment step - tours are now free
    // Payment is the last step (if required)
    // if (!isFinalWalkthrough && !formik.values.skipStripe) {
    //   result.push({ id: 'payment', label: 'Payment' });
    // }

    return result;
  }, [
    isFinalWalkthrough,
    formik.values.skipStripe,
    isAlabamaListing,
    isGeorgiaListing,
  ]);

  const currentStepId = steps[activeStep]?.id;

  // COMMENTED OUT: Coupon/payment logic - tours are now free
  // Update skipStripe based on coupon
  // useEffect(() => {
  //   if (isFinalWalkthrough) {
  //     formik.setFieldValue('skipStripe', true);
  //   } else {
  //     formik.setFieldValue(
  //       'skipStripe',
  //       formik.values.coupon.toLowerCase() === '3forfree' && showings.length < 3
  //     );
  //   }
  // }, [formik.values.coupon, showings.length, isFinalWalkthrough]);

  // Fetch form when entering a document signing step
  useEffect(() => {
    if (currentStepId === 'tourAgreement' && !formik.values.agreementSigned) {
      fetchDocusealForm(BROKER_FORM_ID);
    } else if (
      currentStepId === 'gaDisclosure' &&
      !formik.values.gaDisclosureSigned
    ) {
      fetchDocusealForm(GA_BROKERAGE_DISCLOSURE_FORM_ID);
    } else {
      setFormSlug('');
    }
  }, [currentStepId]);

  const fetchDocusealForm = async (formId: number) => {
    setFormLoading(true);
    try {
      const res = await formsService.post({ listingId, formId });
      setFormSlug(res);
    } finally {
      setFormLoading(false);
    }
  };

  const handleTimesSelected = useCallback(
    (times: Date[]) => {
      formik.setFieldValue('proposedTime', times[0]);
    },
    [formik]
  );

  const processPayment = async (): Promise<boolean> => {
    if (!stripe || !elements) {
      setPaymentError('Payment system not ready. Please try again.');
      return false;
    }

    setProcessingPayment(true);
    setPaymentError(null);

    try {
      const res = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: 'http://localhost:5137/complete',
        },
        redirect: 'if_required',
      });

      if (res.error) {
        setPaymentError(
          res.error.message || 'Payment failed. Please try again.'
        );
        return false;
      }

      if (res.paymentIntent) {
        setPaymentIntentId(res.paymentIntent.id);
        return true;
      }

      return false;
    } catch (e) {
      setPaymentError('An unexpected error occurred. Please try again.');
      return false;
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleNext = async () => {
    // Handle payment step specially
    if (currentStepId === 'payment') {
      const paymentSuccess = await processPayment();
      if (!paymentSuccess) {
        return;
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDocusealComplete = () => {
    if (currentStepId === 'tourAgreement') {
      formik.setFieldValue('agreementSigned', true);
    } else if (currentStepId === 'gaDisclosure') {
      formik.setFieldValue('gaDisclosureSigned', true);
    }
    setFormSlug('');

    // If this is the last step, submit the form
    if (activeStep === steps.length - 1) {
      formik.handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const canProceed = (): boolean => {
    switch (currentStepId) {
      case 'times':
        return !!formik.values.proposedTime;
      case 'payment':
        return true; // Stripe handles its own validation
      case 'tourAgreement':
        return formik.values.agreementSigned;
      case 'gaDisclosure':
        return formik.values.gaDisclosureSigned;
      default:
        return true;
    }
  };

  const isLastStep = activeStep === steps.length - 1;
  const isDocumentStep =
    currentStepId === 'tourAgreement' || currentStepId === 'gaDisclosure';

  if (success) {
    return (
      <Stack direction="column" spacing={2}>
        <Alert severity="success">
          Your request has been received. You will receive a notification when
          your request is accepted.
        </Alert>
        <Button variant="contained" onClick={() => closeModal()}>
          Okay
        </Button>
      </Stack>
    );
  }

  const renderStepContent = () => {
    switch (currentStepId) {
      case 'times':
        return (
          <Box>
            <TimeSelection
              handleTimesSelected={handleTimesSelected}
              timezone={listingTimezone}
            />
            {/* COMMENTED OUT: Coupon field - tours are now free
            {!isFinalWalkthrough && (
              <TextField
                name="coupon"
                value={formik.values.coupon}
                label="Coupon"
                fullWidth
                onChange={formik.handleChange}
                size="small"
                sx={{ mt: 2 }}
                InputProps={{
                  endAdornment: formik.values.skipStripe ? (
                    <InputAdornment position="end">
                      <Check color="success" />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            )}
            */}
          </Box>
        );

      // COMMENTED OUT: Payment step - tours are now free
      case 'payment':
        return (
          <Box>
            {/*
            <Typography variant="body2" color="text.secondary" mb={2}>
              Tour fee: $50 (credited if you close with us)
            </Typography>
            <StripeCheckout />
            {paymentError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {paymentError}
              </Alert>
            )}
            */}
            <Typography variant="body2" color="text.secondary">
              Tours are free with Homepoint!
            </Typography>
          </Box>
        );

      case 'tourAgreement':
        if (formik.values.agreementSigned) {
          return (
            <Alert severity="success">
              Tour Agreement signed successfully!
            </Alert>
          );
        }
        if (formLoading) {
          return (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          );
        }
        if (formSlug) {
          return (
            <DocusealForm
              src={`https://docuseal.co/s/${formSlug}`}
              email={currentUser?.email ?? ''}
              onComplete={handleDocusealComplete}
              expand={false}
              withFieldNames={false}
              withTitle={false}
              signature={`${currentUser?.firstName} ${currentUser?.lastName}`}
              role="Buyer"
            />
          );
        }
        return null;

      case 'gaDisclosure':
        if (formik.values.gaDisclosureSigned) {
          return (
            <Alert severity="success">
              Georgia Brokerage Disclosure signed successfully!
            </Alert>
          );
        }
        if (formLoading) {
          return (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          );
        }
        if (formSlug) {
          return (
            <DocusealForm
              src={`https://docuseal.co/s/${formSlug}`}
              email={currentUser?.email ?? ''}
              onComplete={handleDocusealComplete}
              expand={false}
              withFieldNames={false}
              withTitle={false}
              signature={`${currentUser?.firstName} ${currentUser?.lastName}`}
              role="Buyer"
            />
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <Box>
      {isMobile ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {steps.length}:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {steps[activeStep]?.label}
          </Typography>
        </Stack>
      ) : (
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((step) => (
            <Step key={step.id}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box sx={{ minHeight: 200 }}>{renderStepContent()}</Box>

      <Stack direction="row" justifyContent="space-between" pt={3}>
        <Box display="flex" alignItems="center">
          <img
            src="https://www.nar.realtor/sites/default/files/downloadable/equal-housing-opportunity-logo-1200w.jpg"
            width="40"
            alt="Equal Housing Opportunity"
          />
        </Box>
        <Stack direction="row" spacing={2}>
          {activeStep === 0 ? (
            <Button variant="outlined" color="error" onClick={closeModal}>
              Cancel
            </Button>
          ) : (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          {/* Don't show Next button for document steps - form completion handles progression */}
          {!isDocumentStep && (
            <LoadingButton
              variant="contained"
              onClick={handleNext}
              loading={formik.isSubmitting || processingPayment}
              disabled={!canProceed()}
            >
              {currentStepId === 'payment'
                ? 'Pay & Submit'
                : isLastStep
                ? 'Submit'
                : 'Next'}
            </LoadingButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default FormContainer;
